import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import Conversation from "./Conversation";
import ChatInput from "./ChatInput";
import { useUsersContext } from "../../../context/usersContext";
import { useUserInfo } from "../../../context/user";
import TaskCard from "./WelcomeSections/TaskCard";
import TasksFilterBar from "./WelcomeSections/TasksFilterBar";
import useTaskCardActions from "../hooks/useTaskCardActions";

const formatOutputLabel = (value) => {
  if (!value) return "Unknown";
  return value
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const ConversationWrapper = () => {
  const params = useParams();
  const navigate = useNavigate();
  const userInfo = useUserInfo()[0];
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedTools, setSelectedTools] = useState([]);
  const [prefillMessage, setPrefillMessage] = useState("");

  const {
    selectedAssistant,
    handleSendMessage,
    setMessages,
    createConvesation,
    tasksData,
    fetchTasksData,
    resetAttachedFileSize,
    setIsUserChating,
    contextuploadFilefrominput
  } = useUsersContext();

  useEffect(() => {
    if (selectedFiles.length === 0) resetAttachedFileSize?.();
  }, [selectedFiles]);

  const { bindActions, actionModals } = useTaskCardActions();

  useEffect(() => {
    fetchTasksData?.();
  }, []);

  const handlePrefillConsumed = useCallback(() => setPrefillMessage(""), []);

  const toggleTool = useCallback((tool) => {
    setSelectedTools((prev) =>
      prev.includes(tool) ? prev.filter((t) => t !== tool) : [...prev, tool]
    );
  }, []);

  const clearTools = useCallback(() => setSelectedTools([]), []);

  const toolOptions = useMemo(() => {
    const list = tasksData?.prompt || [];
    const seen = new Map();
    list.forEach((item) => {
      const value = item?.output;
      if (!value || seen.has(value)) return;
      if (value.toLowerCase() === "chatui") return;
      seen.set(value, { value, label: formatOutputLabel(value) });
    });
    return Array.from(seen.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [tasksData]);

  const filteredTasks = useMemo(() => {
    const list = tasksData?.prompt || [];
    const term = search.trim().toLowerCase();
    const normalizedSelected = selectedTools.map((s) =>
      String(s || "").trim().toLowerCase()
    );
    return list.filter((t) => {
      if (normalizedSelected.length > 0) {
        const itemOutput = String(t?.output || "").trim().toLowerCase();
        if (!normalizedSelected.includes(itemOutput)) return false;
      }
      if (!term) return true;
      return (
        (t?.title || "").toLowerCase().includes(term) ||
        (t?.prompt_value || "").toLowerCase().includes(term)
      );
    });
  }, [tasksData, search, selectedTools]);

  const handleTaskCardClick = useCallback((task) => {
    const taskPrompt = task?.prompt_value?.trim();
    if (!taskPrompt) {
      toast.error("This task does not have prompt content to run.");
      return;
    }
    setPrefillMessage(taskPrompt);
  }, []);

  const submitNewMessage = async (
    prompt,
    type = "savetodb",
    messagetype = "notimage",
    setNewMessage,
    addInMessages = true,
  ) => {
    setIsUserChating(true)
    if (selectedFiles.length > 10) {
      const container = document.getElementById("errorofmaxfile");
      if (container) {
        const div = document.createElement("div");
        div.style.display = "flex";
        div.style.justifyContent = "center";
        div.style.position = "relative";
        div.className = "file_upload_input";
        div.innerHTML = `
        <span style="position: absolute; top: 5px; right: 10px; cursor: pointer;" onclick="this.parentElement.remove()">&#10005;</span>
        <p style="padding: 15px; margin: 0px; color: red; text-align: center;">
            You can only upload 10 files at a time
        </p>
      `;
        container.appendChild(div);
        setTimeout(() => {
          div.remove();
        }, 5000);
      }
      return true;
    }

    let resolvedFiles = selectedFiles;
    const pendingFiles = selectedFiles.filter((f) => f instanceof File);
    if (pendingFiles.length > 0) {
      try {
        setIsUserChating(true)
        const response = await contextuploadFilefrominput(
          pendingFiles,
          params?.id,
          () => { },
        );
        console.log("upload response:", response?.data);
        if (response?.data?.success === true && response?.data?.data) {
          const uploaded = Array.isArray(response.data.data)
            ? response.data.data
            : [response.data.data];
          let idx = 0;
          resolvedFiles = selectedFiles
            .map((f) => (f instanceof File ? uploaded[idx++] : f))
            .filter(Boolean);
        } else {
          resolvedFiles = selectedFiles.filter((f) => !(f instanceof File));
        }
      } catch (err) {
        console.error("file upload failed:", err);
        resolvedFiles = selectedFiles.filter((f) => !(f instanceof File));
      }
    }

    const fileToUpload = JSON.parse(JSON.stringify(resolvedFiles));
    const effectivePrompt =
      prompt && prompt.trim()
        ? prompt
        : fileToUpload?.length > 0
          ? ""
          : prompt;
    setSelectedFiles([]);
    if (!params.id) {
      const payload = {
        fileData: fileToUpload?.map((file) => ({
          id: file.id,
          name: file.name,
        })),
        message: effectivePrompt,
        messagetype,
        addInMessages,
      };
      createConvesation(payload);
      setNewMessage && setNewMessage("");
      return;
    }

    const newMsgObject = {
      content: effectivePrompt,
      sender: null,
      time: new Date().toLocaleTimeString(),
      status: "delivered",
      file_type: messagetype,
      assistant_name: selectedAssistant?.assistant_name || "",
      file_ids:
        fileToUpload?.map((file) => ({
          id: file.id,
          name: file.name,
        })) || [],
    };

    const messagePayload = {
      conversation_id: params?.id,
      type: type,
      message: newMsgObject,
    };
    handleSendMessage(messagePayload);
    setNewMessage("");
    addInMessages && setMessages((prev) => [...prev, newMsgObject]);
    document.getElementById("gotovie")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="h-[calc(100vh-85px)] overflow-y-auto max-w-5xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 [&::-webkit-scrollbar]:hidden [scrollbar-width:none] [-ms-overflow-style:none]">
      {!params.id ? (
        <div className="w-full mb-6">
          <div className="text-[24px] text-start w-full mb-4">
            <span className="text-black font-[600] capitalize">
              Hi {userInfo?.userName},
            </span>
            <span className="text-[#9ca3af]  ml-2">
              what would you like done?
            </span>
          </div>
        </div>
      ) : (
        <Conversation
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
          submitNewMessage={submitNewMessage}
        />
      )}

      <div>
        {!params.id && <div id="errorofmaxfile" />}
        <ChatInput
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
          submitNewMessage={submitNewMessage}
          prefillMessage={prefillMessage}
          onPrefillConsumed={handlePrefillConsumed}
        />

        <div className="flex items-center justify-between mt-2 mb-6">
          <p className="text-[12px] text-[#9ca3af]">
            Also available in Slack · Teams{" "}
            <span
              className="underline cursor-pointer"
              onClick={() => navigate("/app/settings")}
            >
              once connected
            </span>
            .
          </p>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border !border-[#e2e8f0] bg-white text-[11px] text-[#6b7280]">
            <span className="w-3 h-3 rounded-sm bg-gradient-to-br from-[#a78bfa] to-[#60a5fa] inline-block" />
            Auto-routed across GPT, Claude, Gemini & more
          </div>
        </div>
      </div>

      {selectedAssistant?.assistant_name !== 'Agent Builder' && <div className="mt-8">
        <TasksFilterBar
          search={search}
          onSearchChange={setSearch}
          selectedTools={selectedTools}
          onToggleTool={toggleTool}
          onClearTools={clearTools}
          toolOptions={toolOptions}
        />

        <div className="grid grid-cols-2 gap-4">
          {filteredTasks.slice(0, 5).map((task) => {
            const isChatUi =
              !task.output || task.output.toLowerCase() === "chatui";
            const tools = isChatUi ? [] : [task.output];
            return (
              <TaskCard
                key={task._id || task.id}
                title={task.title}
                description={task.prompt_value}
                metaLabel={
                  task.repeat && task.repeat !== "none"
                    ? `Repeats ${task.repeat}`
                    : "Run anytime"
                }
                tools={tools}
                buttonLabel="Run this task"
                onClick={() => handleTaskCardClick(task)}
                {...bindActions(task)}
              />
            );
          })}

          {filteredTasks.length - 5 > 0 && (
            <TaskCard
              title={`Explore ${filteredTasks.length - 5}+ tasks`}
              description="Sales, ops, HR, finance and more — or write your own from scratch."
              metaLabel="Ready-made templates"
              buttonLabel="Browse tasks"
              showActions={false}
              onClick={() => navigate("/app/chat/tasks")}
            />
          )}
        </div>
      </div>}

      {actionModals}
    </div>
  );
};

export default ConversationWrapper;
